// NoticeBoardEdit.tsx
import { observer } from "mobx-react-lite";
import MainLayout from "../MainLayout";
import { useLocation, useNavigate } from "react-router-dom";
import { VerticalLayout } from "../../common/common-layout";
import { TitleLargeMedium, TitleMediumBold } from "../../common/common-text";
import { useState } from "react";
import BTBox from "../../common/components/BTBox";
import { BTDateComponent } from "../../common/components/BTDateComponent";
import { BTCheckBox } from "../../common/components/BTCheckBox";
import BTEditor from "../../common/components/board/BTEditor";
import { authStore } from "../../store/AuthStore";
import { baseStore } from "../../store/BaseStore";
import { dialogStore } from "../../store/DialogStore";
import {
  requestDeleteFile,
  requestUpdateNoticeDocuments,
  requestUploadFiles
} from "../../service/BoardService";
import Divider from "@mui/material/Divider";
import theme from "../../common/theme";

const NoticeBoardEdit = () => (
  <MainLayout>
    <NoticeBoardEditBody />
  </MainLayout>
);

export default NoticeBoardEdit;

const NoticeBoardEditBody = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();

  const notice = location.state?.notice;

  const [title, setTitle] = useState(notice?.title || "");
  const [contents, setContents] = useState(notice?.contents || "");
  const [selectedImage, setSelectedImage] = useState<string | null>(notice?.representImage || null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [uploadedFileUrls, setUploadedFileUrls] = useState<string[]>(notice?.attachedFiles || []);
  const [postDate, setPostDate] = useState(notice?.postDate || "");
  const [isPublished, setIsPublished] = useState(notice?.isPublished ?? true);
  const [isAnchored, setIsAnchored] = useState(notice?.isAnchored ?? false);

  const handleSave = async () => {
    try {
      baseStore.showLoading();

      const parser = new DOMParser();
      const docParsed = parser.parseFromString(contents, "text/html");
      const imgElements = docParsed.querySelectorAll("img");

      const docId = notice.id;

      // base64 에디터 이미지 업로드
      const imagesToUpload: File[] = [];
      const base64ImageSrcs: string[] = [];
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src?.startsWith("data:")) base64ImageSrcs.push(src);
      });

      for (const base64Src of base64ImageSrcs) {
        const res = await fetch(base64Src);
        const blob = await res.blob();
        const file = new File([blob], `${Date.now()}_img.png`, { type: blob.type });
        imagesToUpload.push(file);
      }

      const uploadedImageUrls =
        imagesToUpload.length > 0
          ? await requestUploadFiles("notice", docId, "image", imagesToUpload)
          : [];

      let idx = 0;
      imgElements.forEach((img) => {
        const src = img.getAttribute("src");
        if (src?.startsWith("data:")) {
          const newUrl = uploadedImageUrls[idx++];
          if (newUrl) img.setAttribute("src", newUrl);
          if (selectedImage === src) setSelectedImage(newUrl);
        }
      });

      // 이전 본문에 있던 이미지 중 삭제된 것 정리
      const prevDocParsed = notice.contents ? parser.parseFromString(notice.contents, "text/html") : null;
      const prevImgHttpSrcs = prevDocParsed
        ? Array.from(prevDocParsed.querySelectorAll("img"))
            .map((i) => i.getAttribute("src"))
            .filter((s): s is string => !!s && s.startsWith("http"))
        : [];
      const currImgHttpSrcs = Array.from(docParsed.querySelectorAll("img"))
        .map((i) => i.getAttribute("src"))
        .filter((s): s is string => !!s && s.startsWith("http"));

      const deletedImgUrls = prevImgHttpSrcs.filter((prev) => !currImgHttpSrcs.includes(prev));
      await Promise.all(
        deletedImgUrls.map(async (url) => {
          try {
            const match = url.match(/\/o\/([^?]+)\?/);
            if (match) {
              const path = decodeURIComponent(match[1]);
              await requestDeleteFile(path);
            }
          } catch {}
        })
      );

      // 첨부파일 삭제/유지/추가 반영
      const prevAttached: string[] = Array.isArray(notice.attachedFiles) ? notice.attachedFiles : [];
      const currKeepSet = new Set(uploadedFileUrls);
      const deletedFiles = prevAttached.filter((u) => !currKeepSet.has(u));
      await Promise.all(
        deletedFiles.map(async (url) => {
          try {
            const match = url.match(/\/o\/([^?]+)\?/);
            if (match) {
              const path = decodeURIComponent(match[1]);
              await requestDeleteFile(path);
            }
          } catch {}
        })
      );

      const newAttachmentUrls =
        uploadedFiles.length > 0
          ? await requestUploadFiles("notice", docId, "attachments", uploadedFiles)
          : [];

      const finalAttachedFiles = [
        ...uploadedFileUrls.filter((u) => prevAttached.includes(u)),
        ...newAttachmentUrls,
      ];
       
      const imgElements2 = docParsed.querySelectorAll("img");
      imgElements2.forEach((img) => {
        const imgWidth = img.getAttribute("width");
        const imgHeight = img.getAttribute("height");

        if (!imgWidth || !imgHeight) {
          const parentSpan = img.closest("span");
          if (parentSpan) {
            const spanWidth = parentSpan.getAttribute("width");
            const spanHeight = parentSpan.getAttribute("height");

            if (spanWidth) img.setAttribute("width", spanWidth);
            if (spanHeight) img.setAttribute("height", spanHeight);
          }
        }
      });
      
      // 최종 업데이트 데이터 적용
      notice.title = title;
      notice.contents = docParsed.body.innerHTML;
      notice.postDate = postDate;
      notice.isPublished = isPublished;
      notice.isAnchored = isAnchored;
      notice.attachedFiles = finalAttachedFiles;
      notice.updatedBy = authStore.user?.username ?? "";
      notice.updatedAt = new Date();

      await requestUpdateNoticeDocuments(notice);

      dialogStore.showDialog({
        title: "성공",
        message: "공지사항이 성공적으로 수정되었습니다.",
        onConfirm: () => navigate("/board/notice/detail", { state: { notice } }),
        onClose: () => navigate("/board/notice/detail", { state: { notice } }),
      });
    } catch (e) {
      dialogStore.showDialog({
        title: "오류",
        message: "공지사항 수정 중 문제가 발생했습니다. 다시 시도해 주세요.",
        onConfirm: () => { },
      });
    } finally {
      baseStore.hideLoading();
    }
  };

  return (
    <VerticalLayout height="auto" paddingLeft={36} paddingRight={36}>
      <TitleLargeMedium>공지사항 수정</TitleLargeMedium>
      <BTBox height="24px" />
      <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

      <VerticalLayout height={"auto"} paddingBottom={36}>
        {/* 섹션: 공지사항 정보 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>공지사항 정보</TitleMediumBold>
        <BTBox height="16px" />

        {/* 1줄: 게시일자 | 글 공개 여부 | 상단 고정 여부 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexWrap: "nowrap",
            fontSize: "14px",
            color: "#6C757D",
          }}
        >
          <b style={{ whiteSpace: "nowrap" }}>게시일자</b>
          <div style={{ width: 220, flex: "0 0 auto" }}>
            <BTDateComponent width="100%" value={postDate} onChange={setPostDate} />
          </div>

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>글 공개 여부</b>
          <BTCheckBox title="" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />

          <Divider orientation="vertical" sx={{ borderColor: "#6C757D", mx: "4px", height: "16px" }} />

          <b style={{ whiteSpace: "nowrap", flex: "0 0 auto", flexShrink: 0 }}>상단 고정 여부</b>
          <BTCheckBox title="" checked={isAnchored} onChange={(e) => setIsAnchored(e.target.checked)} />
        </div>

        <BTBox height="16px" />
        <Divider style={{ width: "100%", backgroundColor: '#D9D9D9' }} />

        {/* 섹션: 컨텐츠 입력 */}
        <TitleMediumBold style={{ marginTop: "20px" }}>공지사항 컨텐츠 입력</TitleMediumBold>
        <BTBox height="16px" />

        <BTEditor
          title={title}
          contents={contents}
          selectedImage={selectedImage}
          uploadedFiles={uploadedFiles}
          uploadedFileUrls={uploadedFileUrls}
          setTitle={setTitle}
          setContents={setContents}
          setSelectedImage={setSelectedImage}
          setUploadedFiles={setUploadedFiles}
          setUploadedFileUrls={setUploadedFileUrls}
          onSave={handleSave}
          onCancel={() => navigate(-1)}
          attachmentAccept="all"
        />
      </VerticalLayout>
    </VerticalLayout>
  );
});